import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Card,
  Menu,
  Tooltip,
  useTheme,
  alpha,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ListItemText,
  ListItemIcon,
  MenuItem as DropdownMenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Scale as ScaleIcon,
  Diamond as DiamondIcon,
  LocalOffer as LocalOfferIcon,
  Receipt as ReceiptIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DataContext } from '../../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../../styles/ag-grid-premium-theme.css';
import OTPModal from '../../components/OTPModal';
import { OTP_CONFIG } from '../../config/otpConfig';
import { useReactDevTools } from '../../context/ReactDevTools';

const OTP_BYPASS_CONFIG = {
  enabled: true,
  fields: ['huid'],
  customCheck: (changedFields, changes, originalData, newData) => {
    return false;
  }
};

const InventoryPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSecretMode } = useReactDevTools();
  const { stock = [], loading, refresh, updateStock, deleteStock, companyDetails, grossStock, grossStockTransactions } = useContext(DataContext);

  // State for master data from company details
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];
      if (company.items && Array.isArray(company.items)) setItemCategories(company.items);
      if (company.metals && Array.isArray(company.metals)) setMetalTypes(company.metals);
    }
  }, [companyDetails]);

  // Refs so column callbacks stay stable
  const itemCategoriesRef = useRef([]);
  const metalTypesRef = useRef([]);
  useEffect(() => { itemCategoriesRef.current = itemCategories; }, [itemCategories]);
  useEffect(() => { metalTypesRef.current = metalTypes; }, [metalTypes]);

  const getItemShortcut = useCallback((itemName) => {
    if (!itemName) return '';
    const item = itemCategoriesRef.current.find(cat =>
      cat.name.toLowerCase() === itemName.toLowerCase()
    );
    return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  }, []);

  const getMetalShortcut = useCallback((metalName) => {
    if (!metalName) return '';
    const metal = metalTypesRef.current.find(m =>
      m.name.toLowerCase() === metalName.toLowerCase()
    );
    return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  }, []);

  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [originalItemData, setOriginalItemData] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingItemData, setPendingItemData] = useState(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [silverModalOpen, setSilverModalOpen] = useState(false);
  const [duplicateCheckOpen, setDuplicateCheckOpen] = useState(false);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [processingDuplicates, setProcessingDuplicates] = useState({});
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    categories: [],
    metals: [],
    soldStatus: []
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [gridApis, setGridApis] = useState({});
  const debounceTimer = useRef(null);
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Stable refs for handlers used inside columnDefs
  const handleEditRef = useRef(null);
  const handleDeleteRef = useRef(null);
  const handlePrintLabelRef = useRef(null);

  // Keep a ref for selectedItems so callbacks don't depend on it
  const selectedItemsRef = useRef(selectedItems);
  useEffect(() => { selectedItemsRef.current = selectedItems; }, [selectedItems]);

  // Glassmorphism styles from theme — memoized so it doesn't break child memoization
  const glassStyles = useMemo(() => ({
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      WebkitBackdropFilter: 'blur(5px)'
    },
    modal: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
    },
    card: {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.8)'
        : 'rgba(30, 41, 59, 0.7)',
      backdropFilter: 'blur(15px)',
      WebkitBackdropFilter: 'blur(15px)',
      border: '1px solid',
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(148, 163, 184, 0.1)',
      borderRadius: 1,
      boxShadow: theme.shadows[4],
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[5]
      }
    },
    paper: {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(30, 41, 59, 0.4)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid',
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(148, 163, 184, 0.1)',
      borderRadius: 2,
      boxShadow: theme.shadows[4]
    },
    button: {
      light: {
        contained: {
          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
            boxShadow: '0 6px 20px 0 rgba(37, 99, 235, 0.5)'
          }
        }
      },
      dark: {
        contained: {
          background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
          boxShadow: '0 4px 14px 0 rgba(96, 165, 250, 0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            boxShadow: '0 6px 20px 0 rgba(96, 165, 250, 0.45)'
          }
        }
      }
    }
  }), [theme.palette.mode, theme.shadows]);

  const handlePrintLabel = useCallback((item) => {
    if (!item?.qr) {
      setSnackbar({
        open: true,
        message: 'QR code data not found for this item',
        severity: 'warning'
      });
      return;
    }

    const printWindow = window.open('', '_blank');

    const isMRPItem = (parseFloat(item.weight) === 0 &&
      parseFloat(item.grossWeight) === 0 &&
      parseFloat(item.price) > 0);

    const formatNumber = (num) => {
      if (!num || num === "") return "0";
      return parseFloat(num).toFixed(3);
    };

    const getMetalShort = (metal) => getMetalShortcut(metal);

    const getItemType = (name) => {
      if (!name) return '';
      if (name.toLowerCase() === "miscellaneous" && item.miscitemShortcut) {
        return item.miscitemShortcut;
      }
      return getItemShortcut(name);
    };

    function wastageToAlphanumeric(wastage) {
      if (!wastage || wastage === "0" || wastage === "0.0") return "";
      const num = parseFloat(wastage);
      if (isNaN(num)) return "";
      if (Number.isInteger(num)) {
        if (num <= 26) return String.fromCharCode(64 + num);
        return num.toString();
      }
      const wholePart = Math.floor(num);
      const decimalPart = Math.round((num - wholePart) * 100);
      if (wholePart === 0) return decimalPart.toString();
      if (wholePart <= 26) {
        const letter = String.fromCharCode(64 + wholePart);
        if (decimalPart > 0) return letter + decimalPart.toString();
        return letter;
      } else {
        if (decimalPart > 0) return wholePart.toString() + decimalPart.toString();
        return wholePart.toString();
      }
    }

    const getPurity = (melting) => {
      if (!melting) return '92';
      const purity = Math.round(melting / 100 * 22);
      return purity.toString();
    };

    let formattedData;

    if (isMRPItem) {
      formattedData = {
        serialNo: item.serialNo || '0',
        name: item.name || '',
        mrp: parseFloat(item.price).toFixed(2),
        metal: getMetalShort(item.metal),
        metalName: item.metal || '',
        melting: item.melting || '0',
        itemType: getItemType(item.name),
        supplierShortcut: item?.supplierShortcut || '',
        description: item.description || item.mrpDescription || '',
        isMRP: true
      };
    } else {
      formattedData = {
        serialNo: item.serialNo || '0',
        name: item.name || '',
        weight: formatNumber(item.weight),
        grossWeight: formatNumber(item.grossWeight),
        lessWeight: isNaN(formatNumber(item.lessWeight)) ? '0.000' : formatNumber(item.lessWeight),
        metal: getMetalShort(item.metal),
        metalName: item.metal || '',
        huid: item.huid || '',
        makingCharges: item.makingCharges || '15',
        wastage: wastageToAlphanumeric(item.wastage) || '0',
        purity: getPurity(item.melting),
        melting: item.melting || '0',
        itemType: getItemType(item.name),
        supplierShortcut: item?.supplierShortcut || '',
        description: item.description || '',
        isMRP: false
      };
    }

    const qrData = item.qr || '';
    const escapedQrData = qrData.replace(/"/g, '\\"').replace(/'/g, "\\'");

    const generateLabelHTML = () => {
      if (isMRPItem) {
        return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>MRP Label - ${item.serialNo}</title>
          <meta charset="UTF-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: 10cm 1.5cm; margin: 0; padding: 0; }
            html, body {
              margin: 0; padding: 0; width: 10cm; height: 1.5cm;
              overflow: hidden; font-family: Arial, sans-serif; background: white;
              -webkit-print-color-adjust: exact; print-color-adjust: exact;
            }
            #printableLabel {
              width: 10cm; height: 1.5cm; margin: 0; padding: 0;
              display: flex; justify-content: flex-start; align-items: flex-start;
              page-break-after: avoid; page-break-inside: avoid;
            }
            .label-container { width: 10cm; height: 1.5cm; display: flex; }
            .blank-space { width: 3.5cm; height: 1.5cm; }
            .label {
              width: 6.5cm; height: 1.5cm; font-family: Arial, sans-serif;
              font-size: 8px; display: flex; box-sizing: border-box;
            }
            .left, .right {
              width: 3.2cm; height: 1.5cm; display: flex; flex-direction: column;
              justify-content: flex-start; align-items: flex-start; box-sizing: border-box;
            }
            .left { padding: 2px 0 0 5px; text-align: left; }
            .left-inner {
              display: flex; flex-direction: row; align-items: center;
              width: 100%; height: 100%;
            }
            .qr-side { flex: 0 0 35%; display: flex; justify-content: center; align-items: center; }
            .text-side {
              flex: 1; display: flex; flex-direction: column;
              justify-content: center; align-items: flex-start; padding-left: 2px;
            }
            .qr {
              width: 1.2cm; height: 1.2cm; background: #f0f0f0;
              display: flex; align-items: center; justify-content: center;
              color: #999; font-size: 6px; overflow: hidden;
            }
            #qr-canvas { width: 100% !important; height: 100% !important; display: block; }
            .qr-text { font-size: 6px; text-align: center; line-height: 1; }
            .shop-name {
              font-size: 11px; font-weight: 900; color: black;
              text-align: center; width: 100%;
            }
            .right { padding-left: 12px; padding-top: 8px; }
            .details { font-size: 10px; font-weight: 750; }
            .mrp-price { font-size: 12px; font-weight: 700; margin-top: 2px; }
            @media print {
              html, body {
                width: 10cm !important; height: 1.5cm !important;
                margin: 0 !important; padding: 0 !important; overflow: hidden !important;
              }
              body * { visibility: hidden !important; }
              #printableLabel, #printableLabel * { visibility: visible !important; }
              #printableLabel {
                position: absolute !important; top: 0 !important; left: 0 !important;
                width: 10cm !important; height: 1.5cm !important; display: flex !important;
                page-break-after: avoid !important; page-break-inside: avoid !important;
              }
            }
          </style>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
        </head>
        <body>
          <div id="printableLabel">
            <div class="label-container">
              <div class="label">
                <div class="left">
                  <div class="left-inner">
                    <div class="qr-side">
                      <div id="pl-qr" class="qr">
                        <div class="qr-text">Generating QR...</div>
                      </div>
                    </div>
                    <div class="text-side">
                      <div class="shop-name">Aurum Silver</div>
                    </div>
                  </div>
                </div>
                <div class="right">
                  <div class="details">
                    <span>${formattedData.itemType}</span>
                    <span>${formattedData.serialNo}</span>
                    <span> | ${formattedData.metal}</span>
                    <span> | ${formattedData.melting}</span>
                  </div>
                  <div class="mrp-price">MRP: ₹${formattedData.mrp}</div>
                  ${formattedData.description ? `<div style="font-size:8px;color:#64748b;margin-top:1px;">${formattedData.description}</div>` : ''}
                </div>
              </div>
              <div class="blank-space"></div>
            </div>
          </div>
          <script>
            const qrData = "${escapedQrData}";
            if (!qrData || qrData.trim() === '') {
              document.getElementById('pl-qr').innerHTML = '<div class="qr-text">NO QR DATA</div>';
              setTimeout(printDocument, 500);
            } else {
              function generateQRCode() {
                const qrContainer = document.getElementById('pl-qr');
                try {
                  qrContainer.innerHTML = '';
                  const canvas = document.createElement('canvas');
                  canvas.id = 'qr-canvas';
                  qrContainer.appendChild(canvas);
                  if (typeof QRious === 'undefined') throw new Error('QRious library not loaded');
                  new QRious({
                    element: canvas, value: qrData, size: 240, level: 'H',
                    background: 'white', foreground: 'black',
                    backgroundAlpha: 1, foregroundAlpha: 1, padding: 2
                  });
                  canvas.style.width = '100%';
                  canvas.style.height = '100%';
                  setTimeout(printDocument, 500);
                } catch (error) {
                  qrContainer.innerHTML = '<div class="qr-text" style="font-size:6px;line-height:1.2;">QR: ' + qrData.substring(0, 10) + (qrData.length > 10 ? '...' : '') + '</div>';
                  setTimeout(printDocument, 500);
                }
              }
              function printDocument() {
                window.print();
                setTimeout(() => { window.close(); }, 1000);
              }
              function init() {
                if (typeof QRious !== 'undefined') generateQRCode();
                else {
                  let attempts = 0;
                  const maxAttempts = 10;
                  function checkLibrary() {
                    attempts++;
                    if (typeof QRious !== 'undefined') generateQRCode();
                    else if (attempts < maxAttempts) setTimeout(checkLibrary, 100);
                    else {
                      document.getElementById('pl-qr').innerHTML = '<div class="qr-text">QR ERROR</div>';
                      setTimeout(printDocument, 500);
                    }
                  }
                  checkLibrary();
                }
              }
              if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
              else init();
            }
          </script>
        </body>
        </html>`;
      } else {
        return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Jewelry Label - ${item.serialNo}</title>
          <meta charset="UTF-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: 10cm 1.5cm; margin: 0; padding: 0; }
            html, body {
              margin: 0; padding: 0; width: 10cm; height: 1.5cm;
              overflow: hidden; font-family: Arial, sans-serif; background: white;
              -webkit-print-color-adjust: exact; print-color-adjust: exact;
            }
            #printableLabel {
              width: 10cm; height: 1.5cm; margin: 0; padding: 0;
              display: flex; justify-content: flex-start; align-items: flex-start;
              page-break-after: avoid; page-break-inside: avoid;
            }
            .label-container { width: 10cm; height: 1.5cm; display: flex; }
            .blank-space { width: 3.5cm; height: 1.5cm; }
            .label {
              width: 6.5cm; height: 1.5cm; font-family: Arial, sans-serif;
              font-size: 8px; display: flex; box-sizing: border-box;
            }
            .left, .right {
              width: 3.2cm; height: 1.5cm; display: flex; flex-direction: column;
              justify-content: flex-start; align-items: flex-start; box-sizing: border-box;
            }
            .left { padding: 2px 0 0 5px; text-align: left; }
            .left-inner {
              display: flex; flex-direction: row; align-items: center;
              width: 100%; height: 100%;
            }
            .qr-side { flex: 0 0 35%; display: flex; justify-content: center; align-items: center; }
            .text-side {
              flex: 1; display: flex; flex-direction: column;
              justify-content: center; align-items: flex-start; padding-left: 2px;
            }
            .qr {
              width: 1.2cm; height: 1.2cm; background: #f0f0f0;
              display: flex; align-items: center; justify-content: center;
              color: #999; font-size: 6px; overflow: hidden;
            }
            #qr-canvas { width: 100% !important; height: 100% !important; display: block; }
            .qr-text { font-size: 6px; text-align: center; line-height: 1; }
            .shop-name {
              font-size: 11px; font-weight: 900; color: black;
              text-align: center; width: 100%;
            }
            .making {
              text-align: center; width: 100%; font-weight: 700;
              font-size: 10px; margin-top: 1px;
            }
            .right { padding-left: 12px; padding-top: 8px; }
            .details { font-size: 10px; font-weight: 750; }
            .weight { font-size: 10px; font-weight: 750; }
            @media print {
              html, body {
                width: 10cm !important; height: 1.5cm !important;
                margin: 0 !important; padding: 0 !important; overflow: hidden !important;
              }
              body * { visibility: hidden !important; }
              #printableLabel, #printableLabel * { visibility: visible !important; }
              #printableLabel {
                position: absolute !important; top: 0 !important; left: 0 !important;
                width: 10cm !important; height: 1.5cm !important; display: flex !important;
                page-break-after: avoid !important; page-break-inside: avoid !important;
              }
            }
          </style>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
        </head>
        <body>
          <div id="printableLabel">
            <div class="label-container">
              <div class="label">
                <div class="left">
                  <div class="left-inner">
                    <div class="qr-side">
                      <div id="pl-qr" class="qr">
                        <div class="qr-text">Generating QR...</div>
                      </div>
                    </div>
                    <div class="text-side">
                      <div class="shop-name">Aurum Silver</div>
                      <div class="making">Labour: <span>${formattedData.makingCharges}</span></div>
                    </div>
                  </div>
                </div>
                <div class="right">
                  <div class="details">
                    <span>${formattedData.itemType}</span>
                    <span>${formattedData.serialNo}</span>
                    <span> | ${formattedData.supplierShortcut || ''}</span>
                    <span> | ${formattedData.metal}</span>
                    <span> | ${formattedData.melting}</span>
                  </div>
                  <div class="weight">
                    <span>GW-<span>${formattedData.grossWeight}</span></span>
                    <span>LW-<span>${formattedData.lessWeight}</span></span>
                  </div>
                  <div class="weight">NW - <span>${formattedData.weight}</span></div>
                </div>
              </div>
              <div class="blank-space"></div>
            </div>
          </div>
          <script>
            const qrData = "${escapedQrData}";
            if (!qrData || qrData.trim() === '') {
              document.getElementById('pl-qr').innerHTML = '<div class="qr-text">NO QR DATA</div>';
              setTimeout(printDocument, 500);
            } else {
              function generateQRCode() {
                const qrContainer = document.getElementById('pl-qr');
                try {
                  qrContainer.innerHTML = '';
                  const canvas = document.createElement('canvas');
                  canvas.id = 'qr-canvas';
                  qrContainer.appendChild(canvas);
                  if (typeof QRious === 'undefined') throw new Error('QRious library not loaded');
                  new QRious({
                    element: canvas, value: qrData, size: 240, level: 'H',
                    background: 'white', foreground: 'black',
                    backgroundAlpha: 1, foregroundAlpha: 1, padding: 2
                  });
                  canvas.style.width = '100%';
                  canvas.style.height = '100%';
                  setTimeout(printDocument, 500);
                } catch (error) {
                  qrContainer.innerHTML = '<div class="qr-text" style="font-size:6px;line-height:1.2;">QR: ' + qrData.substring(0, 10) + (qrData.length > 10 ? '...' : '') + '</div>';
                  setTimeout(printDocument, 500);
                }
              }
              function printDocument() {
                window.print();
                setTimeout(() => { window.close(); }, 1000);
              }
              function init() {
                if (typeof QRious !== 'undefined') generateQRCode();
                else {
                  let attempts = 0;
                  const maxAttempts = 10;
                  function checkLibrary() {
                    attempts++;
                    if (typeof QRious !== 'undefined') generateQRCode();
                    else if (attempts < maxAttempts) setTimeout(checkLibrary, 100);
                    else {
                      document.getElementById('pl-qr').innerHTML = '<div class="qr-text">QR ERROR</div>';
                      setTimeout(printDocument, 500);
                    }
                  }
                  checkLibrary();
                }
              }
              if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
              else init();
            }
          </script>
        </body>
        </html>`;
      }
    };

    const htmlContent = generateLabelHTML();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    setSnackbar({
      open: true,
      message: isMRPItem
        ? `MRP Label for ${item.serialNo || item.name} printed successfully`
        : `Label for ${item.serialNo || item.name} printed successfully`,
      severity: 'success'
    });
  }, [getItemShortcut, getMetalShortcut]);

  useEffect(() => { handlePrintLabelRef.current = handlePrintLabel; }, [handlePrintLabel]);

  const handleBulkActionClick = (event) => setBulkActionMenuAnchor(event.currentTarget);
  const handleBulkActionClose = () => setBulkActionMenuAnchor(null);

  // ============ FILTERING ============
  const filteredData = useMemo(() => {
    if (!stock.length) return [];

    let result = stock;
    const searchLower = debouncedSearchTerm.toLowerCase();

    if (debouncedSearchTerm) {
      result = result.filter(item => {
        if (item.serialNo && item.serialNo.toString().toLowerCase().includes(searchLower)) return true;
        if (item.name && item.name.toString().toLowerCase().includes(searchLower)) return true;
        if (item.huid && item.huid.toString().toLowerCase().includes(searchLower)) return true;
        if (item.description && item.description.toLowerCase().includes(searchLower)) return true;
        if (item.supplierName && item.supplierName.toLowerCase().includes(searchLower)) return true;
        if (item.weight && item.weight.toString().toLowerCase().includes(searchLower)) return true;
        if (item.lessWeight && item.lessWeight.toString().toLowerCase().includes(searchLower)) return true;
        if (item.grossWeight && item.grossWeight.toString().toLowerCase().includes(searchLower)) return true;
        return false;
      });
    }

    if (filters.categories.length > 0) {
      result = result.filter(item => filters.categories.includes(item.name));
    }

    if (filters.metals.length > 0) {
      result = result.filter(item => filters.metals.includes(item.metal));
    }

    if (filters.soldStatus.length > 0) {
      result = result.filter(item => {
        const isSold = item.sold === "Yes";
        return (filters.soldStatus.includes('sold') && isSold) ||
          (filters.soldStatus.includes('unsold') && !isSold);
      });
    }

    return result;
  }, [stock, debouncedSearchTerm, filters]);

  // Bulk print labels
  const handleBulkPrintLabels = useCallback(async () => {
    if (selectedItems.length === 0) {
      setSnackbar({ open: true, message: 'Please select items to print labels', severity: 'warning' });
      return;
    }

    handleBulkActionClose();
    setBulkActionLoading(true);

    try {
      const selectedItemsData = filteredData.filter(item => selectedItems.includes(item.id));
      if (selectedItemsData.length === 0) throw new Error('No valid items selected');

      const sortedItems = [...selectedItemsData].sort((a, b) =>
        (parseInt(a.serialNo) || 0) - (parseInt(b.serialNo) || 0)
      );

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) throw new Error('Please allow popups to print multiple labels');

      const formatNumber = (num) => (!num || num === "") ? "0" : parseFloat(num).toFixed(3);
      const getMetalShort = (metal) => getMetalShortcut(metal);
      const getItemType = (name, item) => {
        if (!name) return '';
        if (name.toLowerCase() === "miscellaneous" && item.miscitemShortcut) return item.miscitemShortcut;
        return getItemShortcut(name);
      };

      function wastageToAlphanumeric(wastage) {
        if (!wastage || wastage === "0" || wastage === "0.0") return "";
        const num = parseFloat(wastage);
        if (isNaN(num)) return "";
        if (Number.isInteger(num)) {
          if (num <= 26) return String.fromCharCode(64 + num);
          return num.toString();
        }
        const wholePart = Math.floor(num);
        const decimalPart = Math.round((num - wholePart) * 100);
        if (wholePart === 0) return decimalPart.toString();
        if (wholePart <= 26) {
          const letter = String.fromCharCode(64 + wholePart);
          return decimalPart > 0 ? letter + decimalPart.toString() : letter;
        }
        return decimalPart > 0 ? wholePart.toString() + decimalPart.toString() : wholePart.toString();
      }

      const getPurity = (melting) => {
        if (!melting) return '92';
        return Math.round(melting / 100 * 22).toString();
      };

      const isMRPItem = (item) => (
        parseFloat(item.weight) === 0 &&
        parseFloat(item.grossWeight) === 0 &&
        parseFloat(item.price) > 0
      );

      let allLabelsHtml = '';

      sortedItems.forEach((item, index) => {
        const isMRP = isMRPItem(item);
        let formattedData;

        if (isMRP) {
          formattedData = {
            serialNo: item.serialNo || '0',
            name: item.name || '',
            mrp: parseFloat(item.price).toFixed(2),
            metal: getMetalShort(item.metal),
            metalName: item.metal || '',
            melting: item.melting || '0',
            itemType: getItemType(item.name, item),
            supplierShortcut: item?.supplierShortcut || '',
            description: item.description || item.mrpDescription || '',
            isMRP: true
          };
        } else {
          formattedData = {
            serialNo: item.serialNo || '0',
            name: item.name || '',
            weight: formatNumber(item.weight),
            grossWeight: formatNumber(item.grossWeight),
            lessWeight: isNaN(formatNumber(item.lessWeight)) ? '0.000' : formatNumber(item.lessWeight),
            metal: getMetalShort(item.metal),
            huid: item.huid || '',
            makingCharges: item.makingCharges || '15',
            wastage: wastageToAlphanumeric(item.wastage) || '0',
            purity: getPurity(item.melting),
            melting: item.melting || '75',
            itemType: getItemType(item.name, item),
            supplierShortcut: item?.supplierShortcut || '',
            isMRP: false
          };
        }

        let labelContent = '';
        if (isMRP) {
          labelContent = `
          <div class="label-container">
            <div class="label">
              <div class="left">
                <div class="left-inner">
                  <div class="qr-side">
                    <div id="pl-qr-${index}" class="qr"><div class="qr-text">Gen QR...</div></div>
                  </div>
                  <div class="text-side"><div class="shop-name">Aurum Silver</div></div>
                </div>
              </div>
              <div class="right">
                <div class="details">
                  <span class="item-type">${formattedData.itemType}</span>
                  <span class="item-sr-no">${formattedData.serialNo}</span>
                  <span class="item-metal"> | ${formattedData.metal}</span>
                  <span class="item-purity"> | ${formattedData.melting}</span>
                </div>
                <div class="mrp-price">MRP: ₹${formattedData.mrp}</div>
                ${formattedData.description ? `<div class="mrp-description">${formattedData.description}</div>` : ''}
              </div>
            </div>
            <div class="blank-space"></div>
          </div>`;
        } else {
          labelContent = `
          <div class="label-container">
            <div class="label">
              <div class="left">
                <div class="left-inner">
                  <div class="qr-side">
                    <div id="pl-qr-${index}" class="qr"><div class="qr-text">Gen QR...</div></div>
                  </div>
                  <div class="text-side">
                    <div class="shop-name">Aurum Silver</div>
                    <div class="making">Labour: <span class="making-charges">${formattedData.makingCharges}</span></div>
                  </div>
                </div>
              </div>
              <div class="right">
                <div class="details">
                  <span class="item-type">${formattedData.itemType}</span>
                  <span class="item-sr-no">${formattedData.serialNo}</span>
                  <span class="item-supplier"> | ${formattedData.supplierShortcut}</span>
                  <span class="item-metal"> | ${formattedData.metal}</span>
                  <span class="item-purity"> | ${formattedData.melting}</span>
                </div>
                <div class="weight">
                  <span class="gross-weight">GW-<span class="gross-weight-val">${formattedData.grossWeight}</span></span>
                  <span class="less-weight">LW-<span class="less-weight-val">${formattedData.lessWeight}</span></span>
                </div>
                <div class="weight">NW - <span class="net-weight">${formattedData.weight}</span></div>
              </div>
            </div>
            <div class="blank-space"></div>
          </div>`;
        }

        allLabelsHtml += `<div class="label-page">${labelContent}</div>`;
      });

      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Print Labels - ${selectedItems.length} Items</title>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: 10cm 1.5cm; margin: 0; padding: 0; }
    html, body {
      margin: 0; padding: 0; width: 100%; height: 100%;
      font-family: Arial, sans-serif; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .label-page {
      width: 10cm; height: 1.5cm;
      page-break-after: always; page-break-inside: avoid;
    }
    .label-page:last-child { page-break-after: avoid; }
    .label-container { width: 10cm; height: 1.5cm; display: flex; }
    .blank-space { width: 3.5cm; height: 1.5cm; }
    .label {
      width: 6.5cm; height: 1.5cm; font-family: Arial, sans-serif;
      font-size: 8px; display: flex; box-sizing: border-box;
    }
    .left, .right {
      width: 3.2cm; height: 1.5cm; display: flex; flex-direction: column;
      justify-content: flex-start; align-items: flex-start; box-sizing: border-box;
    }
    .left { padding: 2px 0 0 5px; text-align: left; }
    .left-inner { display: flex; flex-direction: row; align-items: center; width: 100%; height: 100%; }
    .qr-side { flex: 0 0 35%; display: flex; justify-content: center; align-items: center; }
    .text-side { flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: flex-start; padding-left: 2px; }
    .qr { width: 1.2cm; height: 1.2cm; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #999; font-size: 6px; overflow: hidden; }
    #qr-canvas { width: 100% !important; height: 100% !important; display: block; }
    .qr-text { font-size: 6px; text-align: center; line-height: 1; }
    .shop-name { font-size: 11px; font-weight: 900; color: black; text-align: center; width: 100%; }
    .making { text-align: center; width: 100%; font-weight: 700; font-size: 10px; margin-top: 1px; }
    .right { padding-left: 12px; padding-top: 8px; }
    .details { font-size: 10px; font-weight: 750; }
    .weight { font-size: 10px; font-weight: 750; }
    .mrp-price { font-size: 12px; font-weight: 700; margin-top: 2px; }
    .mrp-description { font-size: 8px; color: #64748b; margin-top: 1px; }
    @media print {
      html, body { width: 100% !important; height: 100% !important; margin: 0 !important; padding: 0 !important; }
      body * { visibility: visible !important; }
      .label-page {
        page-break-after: always !important; page-break-inside: avoid !important;
        width: 10cm !important; height: 1.5cm !important;
      }
      .label-page:last-child { page-break-after: avoid !important; }
    }
  </style>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
</head>
<body>
  ${allLabelsHtml}
  <script>
    const itemsData = ${JSON.stringify(sortedItems.map(item => ({ qr: item.qr || '' })))};
    function generateAllQRCodes() {
      itemsData.forEach((item, index) => {
        const qrData = item.qr || '';
        const qrContainer = document.getElementById(\`pl-qr-\${index}\`);
        if (qrContainer && qrData && qrData.trim() !== '') {
          try {
            qrContainer.innerHTML = '';
            const canvas = document.createElement('canvas');
            canvas.id = \`qr-canvas-\${index}\`;
            qrContainer.appendChild(canvas);
            if (typeof QRious !== 'undefined') {
              new QRious({
                element: canvas, value: qrData, size: 240, level: 'H',
                background: 'white', foreground: 'black',
                backgroundAlpha: 1, foregroundAlpha: 1, padding: 2
              });
              canvas.style.width = '100%';
              canvas.style.height = '100%';
            } else throw new Error('QRious library not loaded');
          } catch (error) {
            qrContainer.innerHTML = '<div class="qr-text" style="font-size: 6px; line-height: 1.2;">QR: ' + qrData.substring(0, 10) + (qrData.length > 10 ? '...' : '') + '</div>';
          }
        } else if (qrContainer) {
          qrContainer.innerHTML = '<div class="qr-text">NO QR</div>';
        }
      });
      setTimeout(() => { window.print(); setTimeout(() => window.close(), 2000); }, 1000);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', generateAllQRCodes);
    else generateAllQRCodes();
  </script>
</body>
</html>`;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();

      const mrpCount = sortedItems.filter(item => isMRPItem(item)).length;
      const regularCount = sortedItems.length - mrpCount;
      let labelTypeMessage = '';
      if (mrpCount > 0 && regularCount > 0) labelTypeMessage = ` (${mrpCount} MRP, ${regularCount} Regular)`;
      else if (mrpCount > 0) labelTypeMessage = ' (MRP Labels)';
      else labelTypeMessage = ' (Regular Labels)';

      setSnackbar({ open: true, message: `Printing ${selectedItems.length} label(s)${labelTypeMessage}...`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to print labels: ${error.message}`, severity: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedItems, filteredData, getItemShortcut, getMetalShortcut]);

  const executeBulkAction = async (action) => {
    if (selectedItems.length === 0) {
      setSnackbar({ open: true, message: 'Please select items to perform this action', severity: 'warning' });
      return;
    }
    handleBulkActionClose();
    if (action === 'printLabels') { await handleBulkPrintLabels(); return; }
    if (action === 'delete') {
      if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} item(s)? This action cannot be undone.`)) return;
    }
    setBulkActionLoading(true);
    try {
      let result;
      let successMessage = '';
      if (result?.success) {
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
        setSelectedItems([]);
        Object.values(gridApis).forEach(api => { if (api) api.deselectAll(); });
        await refresh();
      } else throw new Error(result?.message || result?.error || 'Bulk action failed');
    } catch (error) {
      setSnackbar({ open: true, message: `Failed: ${error.message}`, severity: 'error' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // ============ AG GRID COLUMN DEFS ============
  // Deps are minimal: [theme] only. Handlers via refs; metalTypes via ref.
  const columnDefs = useMemo(() => [
    {
      headerName: 'SERIAL NO',
      field: 'serialNo',
      width: 140,
      sortable: true,
      sort: 'asc',
      filter: 'agTextColumnFilter',
      cellStyle: { fontWeight: '600', color: theme.palette.text.primary, fontSize: '14px' },
      comparator: (a, b) => (parseInt(a) || 0) - (parseInt(b) || 0)
    },
    {
      headerName: 'NAME',
      field: 'name',
      width: 140,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: { fontWeight: '600', color: theme.palette.text.primary, fontSize: '14px' },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const valA = nodeA.data?.name || '';
        const valB = nodeB.data?.name || '';
        if (valA === 'Miscellaneous' && valB === 'Miscellaneous') {
          return (nodeA.data?.miscitemName || '').localeCompare(nodeB.data?.miscitemName || '');
        }
        return valA.localeCompare(valB);
      },
      valueGetter: (params) => {
        const name = params.data?.name || '';
        const miscitemName = params.data?.miscitemName || '';
        const miscitemShortcut = params.data?.miscitemShortcut || params.data?.itemShortcut || '';
        if (name === 'Miscellaneous' && miscitemName) {
          if (miscitemShortcut) return `Miscellaneous (${miscitemName} - ${miscitemShortcut})`;
          return `Miscellaneous (${miscitemName})`;
        }
        return name;
      }
    },
    {
      headerName: 'GROSS WEIGHT',
      field: 'grossWeight',
      width: 160,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === '') return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px' }
    },
    {
      headerName: 'LESS WEIGHT',
      field: 'lessWeight',
      width: 160,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === '' || params.value === 'NaN') return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px' }
    },
    {
      headerName: 'WEIGHT',
      field: 'weight',
      width: 140,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: { fontWeight: '700', color: theme.palette.primary.main, fontSize: '14px' },
      comparator: (a, b) => parseFloat(a || 0) - parseFloat(b || 0)
    },
    {
      headerName: 'METAL',
      field: 'metal',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        const metal = metalTypesRef.current.find(m => m.name === params.value);
        const shortcut = metal ? metal.shortcut : '';
        const isGold = params.value === 'Gold';
        return (
          <Chip
            label={shortcut ? `${params.value} (${shortcut})` : params.value}
            size="small"
            sx={{
              backgroundColor: isGold ? alpha('#f59e0b', 0.15) : alpha(theme.palette.secondary.main, 0.15),
              color: isGold ? '#92400e' : theme.palette.secondary.main,
              fontWeight: 600,
              border: `1px solid ${isGold ? '#fbbf24' : theme.palette.secondary.main}`,
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'HUID',
      field: 'huid',
      width: 160,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        return (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              color: theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA',
              borderColor: theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA',
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'STATUS',
      field: 'sold',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const isSold = params.value === 'Yes';
        return (
          <Chip
            label={isSold ? 'Sold' : 'Available'}
            size="small"
            sx={{
              backgroundColor: isSold ? alpha('#10b981', 0.15) : alpha('#f59e0b', 0.15),
              color: isSold ? '#065f46' : '#92400e',
              fontWeight: 600,
              border: `1px solid ${isSold ? '#10b981' : '#f59e0b'}`,
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'SUPPLIER',
      field: 'supplierName',
      width: 200,
      sortable: true,
      filter: true,
      cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis', color: theme.palette.text.primary, fontSize: '14px' }
    },
    {
      headerName: 'MELTING %',
      field: 'melting',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px', fontWeight: '600' }
    },
    {
      headerName: 'SILVER RATE',
      field: 'silverRate',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === '') return '-';
        return `${params.value}`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px', fontWeight: '600' }
    },
    {
      headerName: 'LABOUR',
      field: 'labour',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === '') return '-';
        return `${params.value}`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px', fontWeight: '600' }
    },
    {
      headerName: 'GOLD RATE',
      field: 'goldCuttingRate',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px', fontWeight: '600' }
    },
    {
      headerName: 'WASTAGE %',
      field: 'wastage',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px', fontWeight: '600' }
    },
    {
      headerName: 'MAKING CHARGE',
      field: 'makingCharges',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '14px', fontWeight: '600' }
    },
    {
      headerName: 'CREATED AT',
      field: 'created_at',
      width: 180,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        const v = params.value || params.data?.createdAt;
        if (!v) return '-';
        try {
          const date = new Date(v);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleString('en-IN', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
          });
        } catch { return '-'; }
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '13px', fontFamily: 'monospace' }
    },
    {
      headerName: 'UPDATED AT',
      field: 'updated_at',
      width: 180,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        const v = params.value || params.data?.updatedAt;
        if (!v) return '-';
        try {
          const date = new Date(v);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleString('en-IN', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
          });
        } catch { return '-'; }
      },
      cellStyle: { color: theme.palette.text.primary, fontSize: '13px', fontFamily: 'monospace' }
    },
    {
      headerName: 'ACTIONS',
      field: 'actions',
      width: 200,
      pinned: 'right',
      suppressMovable: true,
      cellRenderer: (params) => (
        <Box className="flex space-x-2">
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderRadius: '12px',
                padding: '6px',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2), transform: 'scale(1.1)' },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handleEditRef.current?.(params.data)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha('#ef4444', 0.1),
                color: '#ef4444',
                borderRadius: '12px',
                padding: '6px',
                '&:hover': { backgroundColor: alpha('#ef4444', 0.2), transform: 'scale(1.1)' },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handleDeleteRef.current?.(params.data)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Label" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha('#f59e0b', 0.1),
                color: '#f59e0b',
                borderRadius: '12px',
                padding: '6px',
                '&:hover': { backgroundColor: alpha('#f59e0b', 0.2), transform: 'scale(1.1)' },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handlePrintLabelRef.current?.(params.data)}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ], [theme]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 120,
    suppressMenu: false,
    floatingFilter: false,
    wrapText: true,
    autoHeight: false,
    cellStyle: {
      color: theme.palette.text.primary,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell-premium'
  }), [theme.palette.text.primary]);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 250);
  }, []);

  // ============ STATISTICS ============
  const statistics = useMemo(() => {
    if (!filteredData.length) {
      return { totalItems: 0, soldItems: 0, unsoldItems: 0, totalWeight: '0.00', totalFine: '0.00' };
    }
    let soldItems = 0, totalWeight = 0, totalFine = 0;
    for (let i = 0; i < filteredData.length; i++) {
      const item = filteredData[i];
      const weight = parseFloat(item.weight) || 0;
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      totalWeight += weight;
      totalFine += weight * ((melting + wastage) / 100);
      if (item.sold === 'Yes') soldItems++;
    }
    return {
      totalItems: filteredData.length,
      soldItems,
      unsoldItems: filteredData.length - soldItems,
      totalWeight: totalWeight.toFixed(2),
      totalFine: totalFine.toFixed(2)
    };
  }, [filteredData]);

  // ============ COUNTS (single pass) ============
  const { categoryCounts, metalCounts, soldStatusCounts, categories, metals } = useMemo(() => {
    const catCounts = {};
    const metCounts = {};
    const soldCounts = { sold: 0, unsold: 0 };

    for (let i = 0; i < stock.length; i++) {
      const item = stock[i];
      const category = item.name || 'Uncategorized';
      const metal = item.metal || 'Unknown';
      catCounts[category] = (catCounts[category] || 0) + 1;
      metCounts[metal] = (metCounts[metal] || 0) + 1;
      if (item.sold === 'Yes') soldCounts.sold++;
      else soldCounts.unsold++;
    }

    return {
      categoryCounts: catCounts,
      metalCounts: metCounts,
      soldStatusCounts: soldCounts,
      categories: Object.keys(catCounts).sort(),
      metals: Object.keys(metCounts).sort()
    };
  }, [stock]);

  // ============ GROUPING ============
  const filteredGroupedData = useMemo(() => {
    if (filteredData.length === 0) return {};
    return filteredData.reduce((groups, item) => {
      const category = item.name || 'Uncategorized';
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
      return groups;
    }, {});
  }, [filteredData]);

  const sortedCategoryKeys = useMemo(
    () => Object.keys(filteredGroupedData).sort(),
    [filteredGroupedData]
  );

  // Precompute per-category stats
  const categoryStats = useMemo(() => {
    const stats = {};
    for (const category of Object.keys(filteredGroupedData)) {
      const items = filteredGroupedData[category];
      let weight = 0, fine = 0, sold = 0;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const w = parseFloat(item.weight) || 0;
        const m = parseFloat(item.melting) || 0;
        const ws = parseFloat(item.wastage) || 0;
        weight += w;
        fine += w * ((m + ws) / 100);
        if (item.sold === 'Yes') sold++;
      }
      stats[category] = { weight, fine, sold };
    }
    return stats;
  }, [filteredGroupedData]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const handleCategoryToggle = useCallback((category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
    setTimeout(() => {
      const api = gridApis[category];
      if (api) api.sizeColumnsToFit();
    }, 100);
  }, [gridApis]);

  // ============ GRID HANDLERS (stable) ============
  const handleGridReady = useCallback((category, params) => {
    setGridApis(prev => {
      if (prev[category] === params.api) return prev;
      return { ...prev, [category]: params.api };
    });
    params.api.sizeColumnsToFit();

    const selectedIds = new Set(selectedItemsRef.current);
    params.api.forEachNode(node => {
      if (selectedIds.has(node.data.id)) node.setSelected(true, false);
    });
  }, []);

  const handleSelectionChanged = useCallback((category, items) => {
    const allItemIds = items.map(item => item.id);
    return (params) => {
      const selectedRows = params.api.getSelectedRows();
      const selectedIds = selectedRows.map(row => row.id);
      const selectedSet = new Set(selectedIds);

      setSelectedItems(prev => {
        const newSelected = new Set(prev);
        selectedIds.forEach(id => newSelected.add(id));
        allItemIds.forEach(id => { if (!selectedSet.has(id)) newSelected.delete(id); });
        return Array.from(newSelected);
      });
    };
  }, []);

  // ============ CRUD ============
  const handleEdit = (item) => {
    setEditingItem(item);
    setOriginalItemData({ ...item });
    setEditFormData({ ...item });
    setEditDialogOpen(true);
  };

  const handleSave = (itemId) => {
    const changes = {};
    const changedFields = [];

    Object.keys(editFormData).forEach(key => {
      const originalValue = originalItemData[key] || '';
      const newValue = editFormData[key] || '';
      if (originalValue.toString() !== newValue.toString()) {
        changes[key] = { from: originalValue, to: newValue };
        changedFields.push(key);
      }
    });

    const shouldBypassOTP = checkOTPBypass(changedFields, changes, originalItemData, editFormData);

    if (shouldBypassOTP) {
      executeDirectUpdate(itemId, editFormData, changes);
    } else {
      const pendingData = {
        type: 'edit', itemId, original: originalItemData,
        updated: editFormData, changes, changedFields
      };

      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ACTION, JSON.stringify({
        type: 'edit', itemId, originalData: originalItemData, updatedData: editFormData
      }));
      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ITEM, JSON.stringify({
        original: originalItemData, updated: editFormData
      }));

      setPendingAction('edit');
      setPendingItemData(pendingData);
      setOtpModalOpen(true);
    }
  };

  useEffect(() => { handleEditRef.current = handleEdit; }, []);
  useEffect(() => { handleDeleteRef.current = handleDelete; });

  const checkOTPBypass = (changedFields, changes, originalData, newData) => {
    if (!OTP_BYPASS_CONFIG.enabled) return false;
    if (changedFields.length === 1 && OTP_BYPASS_CONFIG.fields.includes(changedFields[0])) return true;
    if (changedFields.length > 1 && changedFields.every(f => OTP_BYPASS_CONFIG.fields.includes(f))) return true;
    if (OTP_BYPASS_CONFIG.customCheck) return OTP_BYPASS_CONFIG.customCheck(changedFields, changes, originalData, newData);
    return false;
  };

  const executeDirectUpdate = async (itemId, updatedData, changes) => {
    setIsProcessingAction(true);
    try {
      const response = await updateStock(itemId, updatedData);
      if (!response?.success) throw new Error('Failed to update item');
      setSnackbar({
        open: true,
        message: `${Object.keys(changes).join(', ')} updated successfully`,
        severity: 'success'
      });
      setEditDialogOpen(false);
      setEditingItem(null);
      setEditFormData({});
      setOriginalItemData({});
      await refresh();
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to update: ${error.message}`, severity: 'error' });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const checkDuplicateSerialNumbers = useCallback(async () => {
    setIsCheckingDuplicates(true);
    setTimeout(() => {
      try {
        const categorySerialMap = new Map();
        for (let i = 0; i < stock.length; i++) {
          const item = stock[i];
          if (item.sold === 'Yes') continue;
          const serialNo = item.serialNo;
          if (!serialNo || serialNo === '' || serialNo === '0') continue;
          const category = item.name || 'Uncategorized';
          const key = `${category}|${serialNo}`;
          if (!categorySerialMap.has(key)) categorySerialMap.set(key, []);
          categorySerialMap.get(key).push(item);
        }

        const duplicates = [];
        for (const [key, items] of categorySerialMap.entries()) {
          if (items.length > 1) {
            const [category, serialNo] = key.split('|');
            duplicates.push({ category, serialNo, items });
          }
        }

        setDuplicateItems(duplicates);
        if (duplicates.length > 0) setDuplicateCheckOpen(true);
        else setSnackbar({ open: true, message: '✓ No duplicate serial numbers found for unsold items!', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: 'Error checking duplicates. Please try again.', severity: 'error' });
      } finally {
        setIsCheckingDuplicates(false);
      }
    }, 50);
  }, [stock]);

  const debouncedDuplicateCheck = useMemo(() => {
    let timeoutId;
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => checkDuplicateSerialNumbers(), 100);
    };
  }, [checkDuplicateSerialNumbers]);

  const usedSerialNumbersMap = useMemo(() => {
    const map = new Map();
    for (const item of stock) {
      if (item.sold !== 'Yes' && item.serialNo) {
        const category = item.name || 'Uncategorized';
        if (!map.has(category)) map.set(category, new Set());
        map.get(category).add(parseInt(item.serialNo));
      }
    }
    return map;
  }, [stock]);

  const findNextAvailableSerialNumber = useCallback((category) => {
    const usedSerials = usedSerialNumbersMap.get(category) || new Set();
    let nextNumber = 1;
    while (usedSerials.has(nextNumber)) nextNumber++;
    return nextNumber.toString().padStart(3, '0');
  }, [usedSerialNumbersMap]);

  const handleChangeSerialNumber = async (item, duplicateCategory, duplicateSerialNo) => {
    if (processingDuplicates[item.id]) return;
    setProcessingDuplicates(prev => ({ ...prev, [item.id]: true }));
    try {
      const nextSerial = await findNextAvailableSerialNumber(duplicateCategory);
      const updatedData = { ...item, serialNo: nextSerial };
      const response = await updateStock(item.id, updatedData);
      if (response?.success) {
        setSnackbar({
          open: true,
          message: `Serial number changed from ${item.serialNo} to ${nextSerial} for ${item.name || item.serialNo}`,
          severity: 'success'
        });
        await refresh();
        setDuplicateItems(prevDuplicates => {
          return prevDuplicates.map(dupGroup => {
            if (dupGroup.category === duplicateCategory && dupGroup.serialNo === duplicateSerialNo) {
              const updatedItems = dupGroup.items.filter(i => i.id !== item.id);
              if (updatedItems.length <= 1) return null;
              return { ...dupGroup, items: updatedItems };
            }
            return dupGroup;
          }).filter(group => group !== null);
        });
      } else throw new Error('Failed to update serial number');
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to change serial number: ${error.message}`, severity: 'error' });
    } finally {
      setProcessingDuplicates(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const DuplicateCheckDialog = () => (
    <Dialog
      open={duplicateCheckOpen}
      onClose={() => setDuplicateCheckOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(30px)',
          border: '1px solid',
          borderColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(148, 163, 184, 0.2)',
          borderRadius: '20px',
          boxShadow: theme.shadows[6]
        }
      }}
    >
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.2)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: '45px', height: '45px', borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`
            }}>
              <SearchIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Duplicate Serial Numbers Detected</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {duplicateItems.length} issue(s) found with unsold items
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDuplicateCheckOpen(false)}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {duplicateItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)} 0%, ${alpha(theme.palette.success.light, 0.2)} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
            }}>
              <SearchIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
            </Box>
            <Typography variant="h6" sx={{ color: 'success.main', mb: 1 }}>No Duplicates Found!</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              All unsold items have unique serial numbers within their categories.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setDuplicateCheckOpen(false)} sx={{ borderRadius: '10px', textTransform: 'none' }}>
                Close
              </Button>
            </Box>
            <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: '10px' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                ⚠️ <strong>Note:</strong> Only <strong>UNSOLD</strong> items are checked for duplicates.
                Sold items can have the same serial number for record keeping.
                Different categories can have the same serial numbers.
              </Typography>
            </Box>
            {duplicateItems.map((duplicateGroup, index) => (
              <Paper key={index} sx={{
                mb: 3, overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: '12px',
                background: alpha(theme.palette.error.main, 0.05)
              }}>
                <Box sx={{
                  p: 2, background: alpha(theme.palette.error.main, 0.1),
                  borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                    ⚠️ Category: {duplicateGroup.category} | Duplicate Serial: {duplicateGroup.serialNo}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {duplicateGroup.items.length} unsold items share this serial number in the same category
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.1) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Item ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Current Serial No</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>HUID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Metal</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {duplicateGroup.items.map((item, itemIndex) => (
                          <TableRow key={item.id} hover>
                            <TableCell>{item.id?.slice(-6) || item.serialNo}</TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>
                              <Chip label={item.serialNo} size="small" sx={{
                                backgroundColor: alpha(theme.palette.error.main, 0.2),
                                color: theme.palette.error.main,
                                fontWeight: 700, fontFamily: 'monospace'
                              }} />
                            </TableCell>
                            <TableCell>{item.huid || '-'}</TableCell>
                            <TableCell>{item.metal}</TableCell>
                            <TableCell>
                              <Chip label={item.sold === 'Yes' ? 'Sold' : 'Unsold'} size="small" sx={{
                                backgroundColor: item.sold === 'Yes'
                                  ? alpha(theme.palette.info.main, 0.2)
                                  : alpha(theme.palette.warning.main, 0.2),
                                color: item.sold === 'Yes' ? theme.palette.info.main : theme.palette.warning.main
                              }} />
                            </TableCell>
                            <TableCell>
                              {itemIndex === 0 ? (
                                <Chip label="Keep Original" size="small" sx={{
                                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                                  color: theme.palette.success.main
                                }} />
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={processingDuplicates[item.id] ? <CircularProgress size={16} /> : <EditIcon />}
                                  onClick={() => handleChangeSerialNumber(item, duplicateGroup.category, duplicateGroup.serialNo)}
                                  disabled={processingDuplicates[item.id] || item.sold === 'Yes'}
                                  sx={{
                                    borderColor: theme.palette.warning.main,
                                    color: theme.palette.warning.main,
                                    '&:hover': {
                                      borderColor: theme.palette.warning.dark,
                                      backgroundColor: alpha(theme.palette.warning.main, 0.1)
                                    }
                                  }}
                                >
                                  {processingDuplicates[item.id] ? 'Changing...' : 'Change Serial No'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
        <Button onClick={() => setDuplicateCheckOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const getBypassFieldsHelpText = (fieldName) => {
    const bypassFieldsMap = { huid: 'HUID' };
    const fieldLabel = bypassFieldsMap[fieldName];
    if (!fieldLabel) return '';
    const fieldChanged = editFormData[fieldName] !== originalItemData[fieldName];
    const otherChanges = Object.keys(editFormData).some(key =>
      key !== fieldName && editFormData[key] !== originalItemData[key]
    );
    if (fieldChanged && !otherChanges) return `${fieldLabel} updates don't require OTP verification`;
    return '';
  };

  const handleOTPVerificationSuccess = async () => {
    setIsProcessingAction(true);
    try {
      if (pendingAction === 'edit') await executeEditAction();
      else if (pendingAction === 'delete') await executeDeleteAction();
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to ${pendingAction} item: ${error.message}`, severity: 'error' });
    } finally {
      setIsProcessingAction(false);
      if (pendingAction === 'edit') {
        setEditDialogOpen(false);
        setEditingItem(null);
        setEditFormData({});
        setOriginalItemData({});
      }
      setPendingAction(null);
      setPendingItemData(null);
    }
  };

  const executeEditAction = async () => {
    if (!pendingItemData) return;
    const response = await updateStock(pendingItemData.itemId, pendingItemData.updated);
    if (!response?.success) throw new Error('Failed to update item');
    setSnackbar({
      open: true,
      message: `Item ${pendingItemData.updated.serialNo || pendingItemData.updated.name} updated successfully`,
      severity: 'success'
    });
  };

  const executeDeleteAction = async () => {
    if (!pendingItemData) return;
    const response = await deleteStock(pendingItemData.id);
    if (!response?.success) throw new Error('Failed to delete item');
    setSnackbar({
      open: true,
      message: `Item ${pendingItemData.serialNo} deleted successfully`,
      severity: 'success'
    });
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditFormData({});
    setOriginalItemData({});
    setSnackbar({ open: true, message: 'Edit cancelled', severity: 'info' });
  };

  const handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete item ${item.serialNo || item.name}?`)) {
      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ACTION, JSON.stringify({
        type: 'delete', itemId: item.id, itemData: item
      }));
      localStorage.setItem(OTP_CONFIG.STORAGE_KEYS.PENDING_ITEM, JSON.stringify(item));
      setPendingAction('delete');
      setPendingItemData(item);
      setOtpModalOpen(true);
    }
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
      return;
    }
    try {
      const sortedData = [...filteredData].sort((a, b) => {
        if ((a.name || '').localeCompare(b.name || '') !== 0) return (a.name || '').localeCompare(b.name || '');
        return (parseInt(a.serialNo) || 0) - (parseInt(b.serialNo) || 0);
      });

      const headers = ['Serial No.', 'Name', 'Weight', 'Gross Weight', 'Less Weight',
        'Buying Rate', 'Gold Cutting Rate', 'Wastage', 'Supplier Name',
        'Metal', 'Melting', 'HUID', 'Sold', 'Description', 'Item Detail'];

      let csvRows = [];
      let currentCategory = '';

      sortedData.forEach(item => {
        if (item.name !== currentCategory) {
          csvRows.push([`${item.name} (Category)`]);
          csvRows.push(headers);
          currentCategory = item.name;
        }
        csvRows.push([
          item.serialNo || '', item.name || '', item.weight || '', item.grossWeight || '',
          item.lessWeight || '', item.buyingRate || '', item.goldCuttingRate || '',
          item.wastage || '', item.supplierName || '', item.metal || '', item.melting || '',
          item.huid || '', item.sold || '', item.description || '', item.itemDetail || ''
        ]);
      });

      const csvContent = csvRows.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `jewelry_inventory_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Data exported successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to export data', severity: 'error' });
    }
  };

  const handleRefresh = async () => {
    try {
      await refresh();
      setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to refresh data', severity: 'error' });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilters({ categories: [], metals: [], soldStatus: [] });
    setSelectedItems([]);
    Object.values(gridApis).forEach(api => { if (api) api.deselectAll(); });
  };

  const handleSelectAll = () => {
    const allIds = filteredData.map(item => item.id);
    setSelectedItems(allIds);
    Object.values(gridApis).forEach(api => { if (api) api.selectAll(); });
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
    Object.values(gridApis).forEach(api => { if (api) api.deselectAll(); });
  };

  // ============ MEMOIZED STAT CARDS ============
  const statCards = useMemo(() => {
    const stats = [
      { title: 'TOTAL ITEMS', value: statistics.totalItems, Icon: InventoryIcon, gradient: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' },
      { title: 'SOLD ITEMS', value: statistics.soldItems, Icon: TrendingUpIcon, gradient: 'linear-gradient(135deg, #10b981 0%, #047857 100%)' },
      { title: 'UNSOLD ITEMS', value: statistics.unsoldItems, Icon: LocalOfferIcon, gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
      { title: 'TOTAL WEIGHT', value: `${statistics.totalWeight} gm`, Icon: ScaleIcon, gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
      { title: 'TOTAL FINE', value: `${statistics.totalFine} gm`, Icon: DiamondIcon, gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
    ];

    return stats.map(({ title, value, Icon, gradient }, index) => (
      <Grid item xs={12} sm={6} md={2.4} key={index}>
        <Card sx={{ ...glassStyles.card, padding: '7px', height: '100%' }}>
          <Box className="flex items-center justify-between">
            <Box sx={{
              width: '35px', height: '35px', mr: 1, borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: gradient, boxShadow: theme.shadows[2]
            }}>
              <Icon sx={{ color: 'white', fontSize: '20px' }} />
            </Box>
            <Box>
              <Typography variant="caption" className="font-semibold uppercase tracking-wider" sx={{ color: 'text.secondary' }}>
                {title}
              </Typography>
              <Typography variant="h5" className="font-bold mt-2" sx={{ color: 'text.primary' }}>
                {value}
              </Typography>
            </Box>
          </Box>
        </Card>
      </Grid>
    ));
  }, [statistics, glassStyles, theme.shadows]);

  // ============ MEMOIZED CATEGORY LIST ============
  const categoryList = useMemo(() => {
    return sortedCategoryKeys.map((category) => {
      const items = filteredGroupedData[category];
      const isExpanded = expandedCategories[category];
      const stats = categoryStats[category] || { weight: 0, fine: 0, sold: 0 };

      return (
        <Paper key={category} sx={{ ...glassStyles.card, overflow: 'hidden', borderRadius: '16px' }}>
          <Box
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => handleCategoryToggle(category)}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Box className="flex items-center space-x-3">
              <Box sx={{
                width: '40px', height: '40px', borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: theme.shadows[2]
              }}>
                {isExpanded
                  ? <ExpandLessIcon sx={{ color: 'white' }} />
                  : <ExpandMoreIcon sx={{ color: 'white' }} />}
              </Box>
              <Box>
                <Typography variant="h6" className="font-bold" sx={{ color: 'text.primary' }}>
                  {category}
                  {category !== 'Miscellaneous' && ` (${getItemShortcut(category)})`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {stats.sold} sold • {items.length - stats.sold} available
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center space-x-4">
              <Chip label={`${items.length} items`} sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main', fontWeight: 600,
                borderRadius: '8px', backdropFilter: 'blur(10px)'
              }} />
              <Box className="text-right hidden md:block">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Weight: <span className="font-bold" style={{ color: theme.palette.text.primary }}>
                    {stats.weight.toFixed(2)} gm
                  </span>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Fine: <span className="font-bold" style={{ color: theme.palette.text.primary }}>
                    {stats.fine.toFixed(2)} gm
                  </span>
                </Typography>
              </Box>
            </Box>
          </Box>

          {isExpanded && (
            <Box className="p-4">
              <div
                className={`ag-theme-premium-${theme.palette.mode}`}
                style={{
                  height: Math.min(400, Math.max(200, items.length * 50 + 50)),
                  width: '100%', borderRadius: '12px', overflow: 'hidden'
                }}
              >
                <AgGridReact
                  rowData={items}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={(params) => params.data.id}
                  rowSelection="multiple"
                  suppressRowClickSelection={false}
                  suppressCellFocus={true}
                  onGridReady={(params) => handleGridReady(category, params)}
                  onSelectionChanged={handleSelectionChanged(category, items)}
                  pagination={items.length > 50}
                  paginationPageSize={50}
                  domLayout="normal"
                  headerHeight={50}
                  rowHeight={50}
                  suppressScrollOnNewData={true}
                  animateRows={false}
                  rowMultiSelectWithClick={true}
                  suppressRowDeselection={false}
                  rowBuffer={10}
                  debounceVerticalScrollbar={true}
                  cacheQuickFilter={true}
                />
              </div>
            </Box>
          )}
        </Paper>
      );
    });
  }, [
    sortedCategoryKeys,
    filteredGroupedData,
    expandedCategories,
    categoryStats,
    theme,
    columnDefs,
    defaultColDef,
    glassStyles,
    handleGridReady,
    handleSelectionChanged,
    handleCategoryToggle,
    getItemShortcut
  ]);

  return (
    <Box className="min-h-screen">
      <Container maxWidth="xl" className="py-8">
        <Grid container spacing={3}>
          {/* Filters Sidebar */}
          <Grid item size={{ xs: 12, md: 3 }}>
            <Paper sx={{ ...glassStyles.paper, padding: '24px', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <Box className="flex items-center justify-between mb-4">
                <Typography variant="h6" className="font-bold flex items-center" sx={{ color: 'text.primary' }}>
                  <FilterIcon className="mr-2" /> Filters
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{
                    color: 'text.secondary',
                    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                    borderRadius: '12px',
                    '&:hover': { backgroundColor: alpha(theme.palette.text.secondary, 0.2) }
                  }}
                >
                  Clear
                </Button>
              </Box>

              {selectedItems.length > 0 && (
                <Box className="mb-4 p-3 rounded-lg" sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: '12px'
                }}>
                  <Typography variant="subtitle2" className="font-semibold mb-2" sx={{ color: 'primary.main' }}>
                    {selectedItems.length} items selected
                  </Typography>
                  <Box className="flex space-x-2">
                    <Button size="small" variant="outlined" onClick={handleClearSelection} sx={{
                      color: 'primary.main',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderRadius: '8px',
                      '&:hover': { borderColor: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                    }}>Clear</Button>
                    <Button size="small" variant="contained" onClick={handleSelectAll} sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      borderRadius: '8px',
                      '&:hover': { boxShadow: theme.shadows[4] }
                    }}>Select All ({filteredData.length})</Button>
                  </Box>
                </Box>
              )}

              {/* Search Bar */}
              <Box className="mb-6">
                <TextField
                  fullWidth
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      backgroundColor: alpha(theme.palette.text.primary, 0.05),
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.text.primary, 0.2) },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.3) },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main, borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Category Filter */}
              <Box className="mb-6">
                <Typography variant="subtitle2" className="font-semibold mb-2" sx={{
                  color: 'text.primary',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  paddingBottom: '8px'
                }}>📁 CATEGORIES</Typography>
                <FormGroup>
                  {categories.map((category) => (
                    <FormControlLabel
                      key={category}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.categories.includes(category)}
                          onChange={() => handleFilterChange('categories', category)}
                          sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                        />
                      }
                      label={
                        <Box className="flex items-center justify-between w-full" sx={{ width: '100%' }}>
                          <span className="truncate max-w-[120px]" style={{ color: theme.palette.text.primary }}>
                            {category}{category !== 'Miscellaneous' && ` (${getItemShortcut(category)})`}
                          </span>
                          <Chip size="small" label={categoryCounts[category] || 0} sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme?.palette?.mode === 'dark' ? '#c5dbe0' : '#2558a8',
                            fontWeight: 600, borderRadius: '8px', backdropFilter: 'blur(10px)'
                          }} />
                        </Box>
                      }
                      sx={{
                        marginBottom: '4px', width: '100%',
                        '& .MuiFormControlLabel-label': { width: '100%' }
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Metal Filter */}
              <Box className="mb-6">
                <Typography variant="subtitle2" className="font-semibold mb-2" sx={{
                  color: 'text.primary',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  paddingBottom: '8px'
                }}>💎 METAL TYPE</Typography>
                <FormGroup>
                  {metals.map((metal) => (
                    <FormControlLabel
                      key={metal}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.metals.includes(metal)}
                          onChange={() => handleFilterChange('metals', metal)}
                          sx={{ color: 'secondary.main', '&.Mui-checked': { color: 'secondary.main' } }}
                        />
                      }
                      label={
                        <Box className="flex justify-between w-full" sx={{ width: '100%' }}>
                          <span style={{ color: theme.palette.text.primary }}>
                            {metal} ({getMetalShortcut(metal)})
                          </span>
                          <Chip size="small" label={metalCounts[metal] || 0} sx={{
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: 'secondary.main', fontWeight: 600,
                            borderRadius: '8px', backdropFilter: 'blur(10px)'
                          }} />
                        </Box>
                      }
                      sx={{
                        marginBottom: '4px', width: '100%',
                        '& .MuiFormControlLabel-label': { width: '100%' }
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Sold Status Filter */}
              <Box className="mb-6">
                <Typography variant="subtitle2" className="font-semibold mb-2" sx={{
                  color: 'text.primary',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  paddingBottom: '8px'
                }}>📊 SOLD STATUS</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.soldStatus.includes('sold')}
                        onChange={() => handleFilterChange('soldStatus', 'sold')}
                        sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }}
                      />
                    }
                    label={
                      <Box className="flex justify-between w-full">
                        <span style={{ color: theme.palette.text.primary }}>Sold</span>
                        <Chip size="small" label={soldStatusCounts.sold} sx={{
                          backgroundColor: alpha('#10b981', 0.1), color: '#10b981',
                          fontWeight: 600, borderRadius: '8px', backdropFilter: 'blur(10px)'
                        }} />
                      </Box>
                    }
                    sx={{ marginBottom: '4px', width: '100%', '& .MuiFormControlLabel-label': { width: '100%' } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.soldStatus.includes('unsold')}
                        onChange={() => handleFilterChange('soldStatus', 'unsold')}
                        sx={{ color: '#f59e0b', '&.Mui-checked': { color: '#f59e0b' } }}
                      />
                    }
                    label={
                      <Box className="flex justify-between w-full">
                        <span style={{ color: theme.palette.text.primary }}>Not Sold</span>
                        <Chip size="small" label={soldStatusCounts.unsold} sx={{
                          backgroundColor: alpha('#f59e0b', 0.1), color: '#f59e0b',
                          fontWeight: 600, borderRadius: '8px', backdropFilter: 'blur(10px)'
                        }} />
                      </Box>
                    }
                    sx={{ marginBottom: '4px', width: '100%', '& .MuiFormControlLabel-label': { width: '100%' } }}
                  />
                </FormGroup>
              </Box>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item size={{ xs: 12, md: 9 }}>
            <Paper sx={{ ...glassStyles.paper, padding: '25px', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              {/* Header */}
              <Box className="mb-8">
                <Box className="flex items-center justify-between">
                  <Box className="flex items-center space-x-3">
                    <Box sx={{
                      width: '56px', height: '56px', borderRadius: '10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      boxShadow: theme.shadows[3]
                    }}>
                      <InventoryIcon sx={{ color: 'white', fontSize: '28px' }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" className="font-bold" sx={{ color: 'text.primary' }}>
                        Inventory Dashboard
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Manage and monitor your jewelry inventory
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={isCheckingDuplicates ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    disabled={isCheckingDuplicates}
                    onClick={debouncedDuplicateCheck}
                    sx={{
                      ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { transform: 'translateY(-2px)' },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      px: 3, py: 1.5, fontSize: '1rem',
                      boxShadow: theme.shadows[3]
                    }}
                  >
                    {isCheckingDuplicates ? 'Checking...' : 'Check Stock'}
                  </Button>

                  {isSecretMode && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ReceiptIcon />}
                      onClick={() => setSilverModalOpen(true)}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        borderRadius: '10px',
                        px: 3, py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: theme.shadows[3],
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                          boxShadow: theme.shadows[5],
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Gross Stock Details
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Statistics Cards */}
              <Grid container spacing={1.5} className="mb-8">
                {statCards}
              </Grid>

              {/* Toolbar */}
              <Box className="flex flex-wrap items-center justify-between mb-6 gap-3">
                <Box className="flex items-center space-x-3">
                  <Typography variant="h5" className="font-bold" sx={{ color: 'text.primary' }}>
                    Inventory Items
                  </Typography>
                  <Chip label={`${filteredData.length} items`} size="small" sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main', fontWeight: 600,
                    borderRadius: '8px', backdropFilter: 'blur(10px)'
                  }} />
                  {selectedItems.length > 0 && (
                    <Chip label={`${selectedItems.length} selected`} size="small" sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      color: 'white', borderRadius: '8px', backdropFilter: 'blur(10px)'
                    }} />
                  )}
                  {loading && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
                </Box>

                <Box className="flex flex-wrap gap-2">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { transform: 'translateY(-2px)' },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={() => navigate('/addItem')}
                  >
                    Add New Item
                  </Button>

                  {selectedItems.length > 0 && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<MoreVertIcon />}
                        onClick={handleBulkActionClick}
                        sx={{
                          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                          borderRadius: '12px',
                          padding: '10px 20px',
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { transform: 'translateY(-2px)' },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Bulk Actions ({selectedItems.length})
                      </Button>

                      <Menu
                        anchorEl={bulkActionMenuAnchor}
                        open={Boolean(bulkActionMenuAnchor)}
                        onClose={handleBulkActionClose}
                        PaperProps={{
                          sx: {
                            backgroundColor: theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 0.95)'
                              : 'rgba(30, 41, 59, 0.95)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            minWidth: '200px',
                            boxShadow: theme.shadows[4]
                          }
                        }}
                      >
                        <DropdownMenuItem onClick={() => executeBulkAction('printLabels')}>
                          <ListItemIcon><PrintIcon sx={{ color: '#f59e0b' }} /></ListItemIcon>
                          <ListItemText primary="Print Labels" />
                          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                            {selectedItems.length} label(s)
                          </Typography>
                        </DropdownMenuItem>
                      </Menu>
                    </>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.mode === 'dark' ? '#6366f1' : '#0284c7'} 100%)`,
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { transform: 'translateY(-2px)' },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA'} 0%, ${theme.palette.mode === 'light' ? '#7C3AED' : '#8B5CF6'} 100%)`,
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { transform: 'translateY(-2px)' },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={handleExport}
                  >
                    Export Data
                  </Button>
                </Box>
              </Box>

              {/* Category Sections */}
              {loading ? (
                <Box className="text-center py-12">
                  <CircularProgress sx={{ color: 'primary.main' }} />
                  <Typography className="mt-4" sx={{ color: 'text.secondary' }}>
                    Loading inventory data...
                  </Typography>
                </Box>
              ) : filteredData.length === 0 ? (
                <Box className="text-center py-12">
                  <Box sx={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.1)} 0%, ${alpha(theme.palette.text.primary, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                  }}>
                    <SearchIcon sx={{ fontSize: '36px', color: 'text.disabled' }} />
                  </Box>
                  <Typography className="text-lg mb-4" sx={{ color: 'text.primary' }}>No items found</Typography>
                  <Typography className="mb-6" sx={{ color: 'text.secondary' }}>
                    Try adjusting your search or filters
                  </Typography>
                  <Button variant="outlined" sx={{
                    color: 'primary.main',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderRadius: '12px',
                    padding: '8px 24px',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                  }} onClick={handleClearFilters}>Clear All Filters</Button>
                </Box>
              ) : (
                <Box className="space-y-4">
                  {categoryList}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Edit Item Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(148, 163, 184, 0.2)',
            boxShadow: theme.shadows[6],
            borderRadius: '20px'
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          Edit Item: {editingItem?.serialNo || editingItem?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Serial No" value={editFormData.serialNo || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, serialNo: e.target.value }))}
                  size="small" disabled sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Name" value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  size="small" disabled sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Gross Weight (gm)" type="text" value={editFormData.grossWeight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, grossWeight: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Less Weight (gm)" type="text" value={editFormData.lessWeight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, lessWeight: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Weight (gm)" type="text" value={editFormData.weight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, weight: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Metal" value={editFormData.metal || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, metal: e.target.value }))}
                  size="small" sx={{ mb: 2, width: '210px' }}>
                  {metalTypes.map((metal) => (
                    <MenuItem key={metal.id} value={metal.name}>
                      {metal.name} ({metal.shortcut})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="HUID"
                  value={editFormData.huid || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, huid: e.target.value }))}
                  size="small"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': { borderColor: theme.palette.warning.main, borderWidth: '2px' }
                    }
                  }}
                  helperText={getBypassFieldsHelpText('huid')}
                  FormHelperTextProps={{ sx: { color: theme.palette.warning.main, fontWeight: 500 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Status" value={editFormData.sold || 'No'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, sold: e.target.value }))}
                  size="small" sx={{ mb: 2 }}>
                  <MenuItem value="Yes">Sold</MenuItem>
                  <MenuItem value="No">Available</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Supplier Name" value={editFormData.supplierName || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Melting %" type="text" value={editFormData.melting || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, melting: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Buying Rate" type="text" value={editFormData.buyingRate || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, buyingRate: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Gold Rate" type="text" value={editFormData.goldCuttingRate || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, goldCuttingRate: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Wastage %" type="text" value={editFormData.wastage || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, wastage: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Making Charges %" type="text" value={editFormData.makingCharges || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, makingCharges: e.target.value }))}
                  size="small" sx={{ mb: 2 }} />
              </Grid>
              {editFormData.metal === 'Silver' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Silver Rate" type="text" value={editFormData.silverRate || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, silverRate: e.target.value }))}
                      size="small" sx={{ mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Labour" type="text" value={editFormData.labour || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, labour: e.target.value }))}
                      size="small" sx={{ mb: 2 }} />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          background: alpha(theme.palette.text.primary, 0.05),
          padding: '16px 24px',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          <Button onClick={handleCancelEdit} sx={{
            color: 'text.secondary',
            borderColor: alpha(theme.palette.divider, 0.5),
            borderRadius: '12px',
            padding: '8px 20px',
            '&:hover': { backgroundColor: alpha(theme.palette.text.secondary, 0.1) }
          }}>Cancel</Button>
          <Button onClick={() => handleSave(editingItem?.id)} variant="contained" sx={{
            ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
            borderRadius: '12px',
            padding: '8px 24px',
            fontWeight: 600,
            '&:hover': { transform: 'translateY(-1px)' },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            backdropFilter: 'blur(20px)',
            backgroundColor: alpha(
              snackbar.severity === 'success' ? theme.palette.success.main :
                snackbar.severity === 'error' ? theme.palette.error.main :
                  snackbar.severity === 'warning' ? theme.palette.warning.main :
                    theme.palette.info.main,
              0.9
            ),
            color: 'white',
            borderRadius: '12px',
            boxShadow: theme.shadows[4],
            border: '1px solid',
            borderColor: alpha(
              snackbar.severity === 'success' ? theme.palette.success.main :
                snackbar.severity === 'error' ? theme.palette.error.main :
                  snackbar.severity === 'warning' ? theme.palette.warning.main :
                    theme.palette.info.main,
              0.5
            )
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isProcessingAction && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
            Processing {pendingAction}...
          </Typography>
        </Box>
      )}

      {bulkActionLoading && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, flexDirection: 'column', gap: 2
        }}>
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Processing bulk operation...
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
            Please wait while we update {selectedItems.length} item(s)
          </Typography>
        </Box>
      )}

      <OTPModal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setPendingAction(null);
          setPendingItemData(null);
        }}
        actionType={pendingAction}
        itemData={pendingItemData}
        onVerifySuccess={handleOTPVerificationSuccess}
      />

      {/* Gross Stock Details Dialog */}
      <Dialog
        open={silverModalOpen}
        onClose={() => setSilverModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            overflow: 'hidden',
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle sx={{
          ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
          color: 'white',
          padding: '20px 24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: '50px', height: '50px', borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  ...(theme.palette.mode === 'light' ? glassStyles.button.light.contained : glassStyles.button.dark.contained),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <InventoryIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>
                    Gross Stock Details
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Track all stock items with piece and weight tracking
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setSilverModalOpen(false)}
                sx={{
                  color: 'white',
                  background: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { background: 'rgba(255, 255, 255, 0.3)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{
            p: 3,
            background: alpha(theme.palette.background.paper, 0.8),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Stock Items Overview
              </Typography>
              <Chip label={`${grossStock?.length || 0} items`} size="small" sx={{
                background: alpha(theme.palette.primary.main, 0.2),
                color: theme.palette.primary.main, fontWeight: 600
              }} />
            </Box>

            <Paper sx={{
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '12px',
              background: alpha(theme.palette.background.paper, 0.5)
            }}>
              <TableContainer sx={{ maxHeight: '300px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
                    }}>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5 }}>Item Name</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5 }}>Metal</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5, textAlign: 'center' }}>Current Stock (Pieces)</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', py: 1.5, textAlign: 'center' }}>Current Weight (g)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grossStock && grossStock.length > 0 ? (
                      grossStock.map((item) => (
                        <TableRow key={item.id} hover sx={{ '&:hover': { background: alpha(theme.palette.primary.main, 0.05) } }}>
                          <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{item.itemName}</TableCell>
                          <TableCell>
                            <Chip label={item.metal || 'N/A'} size="small" sx={{
                              background: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main, fontWeight: 600, fontSize: '0.75rem'
                            }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: 'primary.main' }}>
                            {item.currentStock || 0}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: 'primary.main' }}>
                            {parseFloat(item.currentWeight || 0).toFixed(2)} gm
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">No gross stock items found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, minHeight: '400px' }}>
            {/* Stock Added */}
            <Box sx={{
              flex: 1,
              borderRight: { lg: `1px solid ${alpha(theme.palette.divider, 0.2)}` },
              borderBottom: { xs: `1px solid ${alpha(theme.palette.divider, 0.2)}`, lg: 'none' }
            }}>
              <Box sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.1)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ color: theme.palette.success.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>Stock Added</Typography>
                    <Chip
                      label={`${grossStockTransactions?.filter(t => t.operation === 'add').length || 0} entries`}
                      size="small"
                      sx={{
                        background: alpha(theme.palette.success.main, 0.2),
                        color: theme.palette.success.main, fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: '300px', overflow: 'auto', p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: alpha(theme.palette.success.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Pieces</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Weight</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Party</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grossStockTransactions?.filter(t => t.operation === 'add').map((row) => {
                        const item = grossStock?.find(item => item.id === row.itemId);
                        const itemName = item?.itemName || row.itemName || row.itemId || 'Unknown';
                        return (
                          <TableRow key={row.id} hover sx={{ '&:hover': { background: alpha(theme.palette.success.main, 0.05) } }}>
                            <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{itemName}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary' }}>{row.numberOfPieces || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary', fontWeight: 700 }}>
                              {parseFloat(row.weightChanged || 0).toFixed(2)} gm
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary' }}>{row.partyName || 'N/A'}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(!grossStockTransactions || grossStockTransactions.filter(t => t.operation === 'add').length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="text.secondary">No stock added transactions</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            {/* Stock Sold */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.dark, 0.1)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveIcon sx={{ color: theme.palette.error.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>Stock Sold</Typography>
                    <Chip
                      label={`${grossStockTransactions?.filter(t => t.operation === 'subtract').length || 0} entries`}
                      size="small"
                      sx={{
                        background: alpha(theme.palette.error.main, 0.2),
                        color: theme.palette.error.main, fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: '300px', overflow: 'auto', p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: alpha(theme.palette.error.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Pieces</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Weight</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Party</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grossStockTransactions?.filter(t => t.operation === 'subtract').map((row) => {
                        const item = grossStock?.find(item => item.id === row.itemId);
                        const itemName = item?.itemName || row.itemName || row.itemId || 'Unknown';
                        return (
                          <TableRow key={row.id} hover sx={{ '&:hover': { background: alpha(theme.palette.error.main, 0.05) } }}>
                            <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{itemName}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary' }}>{row.numberOfPieces || 0}</TableCell>
                            <TableCell sx={{ textAlign: 'center', color: 'text.primary', fontWeight: 700 }}>
                              {parseFloat(row.weightChanged || 0).toFixed(2)} gm
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary' }}>{row.partyName || 'N/A'}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(!grossStockTransactions || grossStockTransactions.filter(t => t.operation === 'subtract').length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="text.secondary">No stock sold transactions</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          background: alpha(theme.palette.background.paper, 0.5)
        }}>
          <Button onClick={() => setSilverModalOpen(false)} sx={{
            color: 'text.secondary', borderRadius: '10px', px: 3,
            '&:hover': { background: alpha(theme.palette.text.secondary, 0.1) }
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      <DuplicateCheckDialog />
    </Box>
  );
};

export default InventoryPage;